import * as React from "react";
import { Link } from "@tanstack/react-router";
import type { Icon as PhosphorIcon } from "@phosphor-icons/react";
import { cn } from "~/lib/utils";
import { useSidebarCollapsed } from "./SidebarNav";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

interface SidebarItemProps {
  to: string;
  label: string;
  icon: PhosphorIcon;
  active?: boolean | undefined;
  badge?: React.ReactNode;
}

export function SidebarItem({
  to,
  label,
  icon: Icon,
  active,
  badge,
}: SidebarItemProps) {
  const collapsed = useSidebarCollapsed();
  const inner = (
    <Link
      to={to}
      className={cn(
        "group flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-[background-color,color,transform] duration-[var(--dur-quick)] ease-[var(--ease-out-quart)]",
        active
          ? "bg-white/10 text-sidebar-foreground"
          : "text-sidebar-foreground/70 hover:bg-white/5 hover:text-sidebar-foreground",
        collapsed && "justify-center px-0",
      )}
    >
      <Icon
        size={18}
        weight={active ? "fill" : "regular"}
        className={cn(
          "shrink-0 transition-transform",
          active ? "text-sidebar-accent" : "text-sidebar-foreground/70",
          collapsed ? "" : "group-hover:scale-110",
        )}
      />
      {!collapsed && <span className="truncate">{label}</span>}
      {!collapsed && badge ? (
        <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider text-sidebar-foreground/60">
          {badge}
        </span>
      ) : null}
    </Link>
  );

  if (!collapsed) return inner;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>{inner}</TooltipTrigger>
        <TooltipContent side="right">{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
